import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, BarChart3, ExternalLink, Trash2, TrendingUp, Copy, Check, FileText, Calendar, Share, ChevronDown, MoreVertical, Pencil, LayoutTemplate } from 'lucide-react';
import { buttonStyles } from '../../utils/colors';
import useFormList from '../../hooks/useFormList';
import useFormTemplates from '../../hooks/useFormTemplates';
import FormTemplateGallery from '../../components/FormTemplateGallery';
import SkeletonFormList from '../../components/skeletons/SkeletonFormList';

export default function FastestFormFormList() {
  const {
    forms, loading, showCreateModal, newFormTitle, newFormDescription,
    copiedFormId, openShareDropdown, openActionsDropdown, highlightedFormId,
    shareDropdownRefs, actionsDropdownRefs,
    setShowCreateModal, setNewFormTitle, setNewFormDescription,
    setOpenShareDropdown,
    createForm, createFormFromTemplate, deleteForm, publishForm, unpublishForm, copyPublicLink,
    getPublicUrl, handleRowClick, toggleShareDropdown, toggleActionsDropdown,
    duplicateForm, renameForm,
  } = useFormList();

  const {
    templates, categories, loading: templatesLoading,
    applyTemplate, deleteTemplate: deleteFormTemplate,
  } = useFormTemplates();

  const [renamingForm, setRenamingForm] = React.useState(null);
  const [showCreateChoice, setShowCreateChoice] = React.useState(false);
  const [showTemplateGallery, setShowTemplateGallery] = React.useState(false);
  const renameModalRef = React.useRef(null);

  if (loading) return <SkeletonFormList />;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mt-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Forms</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-400">
            Create and manage your forms
          </p>
        </div>
        <div>
          <button
            onClick={() => setShowCreateChoice(true)}
            className={`${buttonStyles.primary} inline-flex items-center whitespace-nowrap`}
          >
            <Plus className="w-4 h-4 mr-2" />
            New Form
          </button>
        </div>
      </div>

      {/* Forms List */}
      <div className="mt-8 bg-white shadow-sm rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
        {!Array.isArray(forms) || forms.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">No forms yet</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Get started by creating your first form.</p>
            <button
              onClick={() => setShowCreateChoice(true)}
              className={`mt-6 ${buttonStyles.primary} inline-flex items-center`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Form
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 overflow-visible">
            {/* Header */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 rounded-t-lg dark:bg-gray-700 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Forms</h2>
                <span className="text-sm text-gray-500 dark:text-gray-300">{forms.length} form{forms.length !== 1 ? 's' : ''}</span>
              </div>
            </div>

            {/* Forms List */}
            {forms.map((form) => (
              <div
                key={form.id}
                data-form-id={form.id}
                className={`px-6 py-4 hover:bg-gray-50 transition-all duration-300 cursor-pointer dark:hover:bg-gray-700 dark:border-gray-600 ${
                  highlightedFormId === form.id
                    ? 'bg-gradient-to-r from-brand-50 to-green-50 dark:from-brand-900/30 dark:to-green-900/30 animate-pulse shadow-lg border-l-4 border-brand-100 dark:ring-brand-700'
                    : ''
                }`}
                onClick={(e) => handleRowClick(form.id, e)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center dark:bg-brand-700">
                        <FileText className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-medium text-gray-900 truncate dark:text-gray-100">{form.title}</h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            form.status === 'published'
                              ? 'bg-brand-100 text-brand-800 dark:bg-brand-700 dark:text-brand-300'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-700 dark:text-amber-300'
                          }`}>
                            {form.status}
                          </span>
                          {form.current_version && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-300">
                              v{form.current_version}
                            </span>
                          )}
                        </div>
                        {form.description && (
                          <p className="mt-1 text-sm text-gray-500 truncate dark:text-gray-400">{form.description}</p>
                        )}
                        <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>Created {new Date(form.created_at).toLocaleDateString()}</span>
                          </div>
                          {form.status === 'published' && (
                            <div className="flex items-center space-x-1">
                              <ExternalLink className="w-3 h-3" />
                              <span>Ready to share with respondents</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {/* Primary Actions */}
                    <div className="flex items-center space-x-1">
                      <Link
                        to={`/forms/${form.id}/responses`}
                        className="action-button inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <BarChart3 className="w-4 h-4 mr-1" />
                        Responses
                      </Link>

                      <Link
                        to={`/forms/${form.id}/analytics`}
                        className="action-button inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TrendingUp className="w-4 h-4 mr-1" />
                        Analytics
                      </Link>

                      {/* Share Dropdown */}
                      {form.status === 'published' && (
                        <div className="relative action-dropdown" ref={el => shareDropdownRefs.current[form.id] = el}>
                          <button
                            onClick={(e) => toggleShareDropdown(form.id, e)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
                          >
                            <Share className="w-4 h-4 mr-1" />
                            Share
                            <ChevronDown className="w-3 h-3 ml-1" />
                          </button>

                          {openShareDropdown === form.id && (
                            <div className="absolute right-0 z-50 mt-2 w-56 rounded-md bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none border dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">
                              <div className="py-1">
                                <a
                                  href={getPublicUrl(form.id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenShareDropdown(null);
                                  }}
                                >
                                  <ExternalLink className="w-4 h-4 mr-3 text-gray-400 group-hover:text-gray-500 flex-shrink-0" />
                                  <div className="flex-1">
                                    <div className="font-medium leading-tight">Preview Public</div>
                                    <div className="text-xs mt-0.5 leading-tight">View form as respondents see it</div>
                                  </div>
                                </a>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyPublicLink(form.id);
                                    setOpenShareDropdown(null);
                                  }}
                                  className="group flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 text-left"
                                >
                                  {copiedFormId === form.id ? (
                                    <Check className="w-4 h-4 mr-3 text-brand-600 flex-shrink-0" />
                                  ) : (
                                    <Copy className="w-4 h-4 mr-3 text-gray-400 group-hover:text-gray-500 flex-shrink-0" />
                                  )}
                                  <div className="flex-1">
                                    <div className="font-medium leading-tight dark:text-gray-200">
                                      {copiedFormId === form.id ? 'Copied!' : 'Copy Public Link'}
                                    </div>
                                    <div className="text-xs mt-0.5 leading-tight dark:text-gray-400">Share link with respondents</div>
                                  </div>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Publish button for drafts */}
                      {form.status === 'draft' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            publishForm(form.id);
                          }}
                          className={`action-button ${buttonStyles.primary} px-3 py-1.5 text-sm`}
                        >
                          Publish
                        </button>
                      )}
                    </div>

                    {/* Actions dropdown (3 dots menu) */}
                    <div className="relative action-dropdown" ref={el => actionsDropdownRefs.current[form.id] = el}>
                      <button
                        onClick={(e) => toggleActionsDropdown(form.id, e)}
                        className="action-button p-1.5 text-gray-400 hover:text-gray-600 transition-colors dark:text-gray-400 dark:hover:text-gray-300"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>

                      {openActionsDropdown === form.id && (
                        <div className="absolute right-0 z-50 mt-2 w-48 rounded-md bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none border dark:bg-gray-800 dark:border-gray-700">
                          <div className="py-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setRenamingForm(form);
                              }}
                              className="group flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <Pencil className="w-4 h-4 mr-3 text-gray-400 group-hover:text-gray-500 flex-shrink-0 dark:text-gray-400 dark:group-hover:text-gray-300" />
                              Rename
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                duplicateForm(form.id);
                              }}
                              className="group flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <Copy className="w-4 h-4 mr-3 text-gray-400 group-hover:text-gray-500 flex-shrink-0 dark:text-gray-400 dark:group-hover:text-gray-300" />
                              Duplicate Form
                            </button>

                            {form.status === 'published' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  unpublishForm(form.id);
                                }}
                                className="group flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                              >
                                <FileText className="w-4 h-4 mr-3 text-gray-400 group-hover:text-gray-500 flex-shrink-0 dark:text-gray-400 dark:group-hover:text-gray-300" />
                                Unpublish
                              </button>
                            )}

                            <div className="border-t border-gray-100 dark:border-gray-600"></div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteForm(form.id);
                              }}
                              className="group flex w-full items-center px-4 py-2 text-sm text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                            >
                              <Trash2 className="w-4 h-4 mr-3 text-red-400 group-hover:text-red-500 flex-shrink-0 dark:text-red-400 dark:group-hover:text-red-300" />
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Choice Modal */}
      {showCreateChoice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="absolute inset-0 z-20" onClick={() => setShowCreateChoice(false)}></div>
          <div className="relative top-20 z-50 mx-auto p-5 border max-w-md shadow-lg rounded-md bg-white dark:bg-gray-800 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 mb-2 dark:text-gray-200">Create New Form</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">How would you like to start?</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setShowCreateChoice(false); setShowCreateModal(true); }}
                className="flex flex-col items-center gap-3 p-5 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-brand-300 hover:bg-brand-50/50 dark:hover:bg-brand-900/20 transition-all group text-center"
              >
                <div className="w-11 h-11 rounded-lg bg-gray-100 dark:bg-gray-700 group-hover:bg-brand-100 dark:group-hover:bg-brand-900/50 flex items-center justify-center transition-colors">
                  <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-brand-600 transition-colors" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Start from scratch</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Build your form field by field</div>
                </div>
              </button>
              <button
                onClick={() => { setShowCreateChoice(false); setShowTemplateGallery(true); }}
                className="flex flex-col items-center gap-3 p-5 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-brand-300 hover:bg-brand-50/50 dark:hover:bg-brand-900/20 transition-all group text-center"
              >
                <div className="w-11 h-11 rounded-lg bg-gray-100 dark:bg-gray-700 group-hover:bg-brand-100 dark:group-hover:bg-brand-900/50 flex items-center justify-center transition-colors">
                  <LayoutTemplate className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-brand-600 transition-colors" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Use a template</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Choose from ready-made forms</div>
                </div>
              </button>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowCreateChoice(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Blank Form Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="absolute inset-0 z-20" onClick={() => setShowCreateModal(false)}></div>
          <div className="relative top-20 z-50 mx-auto p-5 border max-w-xl shadow-lg rounded-md bg-white dark:bg-gray-800 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 mb-4 dark:text-gray-200">Create Blank Form</h3>
            <form onSubmit={createForm}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">
                  Form Title
                </label>
                <input
                  type="text"
                  value={newFormTitle}
                  onChange={(e) => setNewFormTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">
                  Description (Optional)
                </label>
                <textarea
                  value={newFormDescription}
                  onChange={(e) => setNewFormDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  rows="3"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-600 border border-transparent rounded-md hover:bg-brand-700"
                >
                  Create Form
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Template Gallery Modal */}
      <FormTemplateGallery
        isOpen={showTemplateGallery}
        onClose={() => setShowTemplateGallery(false)}
        templates={templates}
        categories={categories}
        loading={templatesLoading}
        onDeleteTemplate={deleteFormTemplate}
        onSelectTemplate={async (template) => {
          const newForm = await applyTemplate(template.id);
          if (newForm) {
            createFormFromTemplate(newForm);
            setShowTemplateGallery(false);
          }
        }}
      />

      {/* Rename Form Modal */}
      {renamingForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="absolute inset-0 z-20" onClick={() => setRenamingForm(null)}></div>
          <div ref={renameModalRef} className="relative top-20 z-50 mx-auto p-5 border max-w-md shadow-lg rounded-md bg-white dark:bg-gray-800 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 mb-4 dark:text-gray-200">Rename Form</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const newTitle = e.target.elements.formTitle.value.trim();
              if (newTitle) {
                renameForm(renamingForm.id, newTitle);
                setRenamingForm(null);
              }
            }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">
                  Form Title
                </label>
                <input
                  type="text"
                  name="formTitle"
                  defaultValue={renamingForm.title}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  required
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setRenamingForm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-600 border border-transparent rounded-md hover:bg-brand-700"
                >
                  Rename
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
